import React from "react";
import { Navigate } from "react-router-dom";
import usePermissions from "../hooks/usePermissions";
import { AlertCircle, Lock, ArrowLeft } from "lucide-react";

/**
 * Protected Route Component
 * Wraps pages/components that require specific permissions
 */
const ProtectedRoute = ({
  children,
  permission,
  action,
  page,
  requireAll = false,
  redirectTo = "/dashboard",
  showFallback = true,
}) => {
  const {
    hasPermission,
    hasAnyPermission,
    canAccessPage,
    canPerformAction,
    loading,
    userRole,
  } = usePermissions();

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Check if user is logged in
  if (!userRole) {
    return <Navigate to="/login" replace />;
  }

  let hasAccess = false;

  // Check different types of permissions
  if (action) {
    hasAccess = canPerformAction(action);
  } else if (page) {
    hasAccess = canAccessPage(page);
  } else if (permission) {
    if (Array.isArray(permission)) {
      if (requireAll) {
        hasAccess = permission.every((perm) => hasPermission(perm));
      } else {
        hasAccess = hasAnyPermission(permission);
      }
    } else {
      hasAccess = hasPermission(permission);
    }
  } else {
    // No specific permission required
    hasAccess = true;
  }

  // If user doesn't have access
  if (!hasAccess) {
    if (!showFallback) {
      return <Navigate to={redirectTo} replace />;
    }

    // Show access denied page
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Access Denied
            </h1>
            <p className="text-gray-600 mb-6">
              You don't have permission to access this page. Please contact your
              administrator if you believe this is an error.
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Your Role:</span> {userRole}
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <button
                  onClick={() => (window.location.href = "#/dashboard")}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
                >
                  Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User has access, render the protected content
  return <>{children}</>;
};

/**
 * Role-based Route Component
 * Only allows specific roles to access the route
 */
export const RoleBasedRoute = ({
  children,
  allowedRoles = [],
  redirectTo = "/dashboard",
  showFallback = true,
}) => {
  const { userRole, loading } = usePermissions();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  if (!userRole) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(userRole)) {
    if (!showFallback) {
      return <Navigate to={redirectTo} replace />;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Role Restricted
            </h1>
            <p className="text-gray-600 mb-6">
              This page is only accessible to users with specific roles.
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Your Role:</span> {userRole}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Required Roles:</span>{" "}
                  {allowedRoles.join(", ")}
                </p>
              </div>
              <button
                onClick={() => (window.location.href = "#/dashboard")}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
